import { Fragment, useEffect, useState } from "react";
import classnames from "classnames";
import { decode } from "he";
import { SideBarLink } from "./SideBarLink";
import { SideBarLinkButton } from "./SideBarLinkButton";
import { SideBarHorizontalDivider } from "./SideBarHorizontalDivider";
import { PopupContextMenuLink } from "../PopupContextMenu/PopupContextMenuLink";
import { PopupContextMenuLinkButton } from "../PopupContextMenu/PopupContextMenuLinkButton";
import ResponsiveLayout from "../../../../components/ResponsiveLayout";
import { breakpoints } from "../../../../core/hooks/dimensionProvider";
import useBranding from "../../../../core/branding/useBranding";
import PrimeModal from "../../PrimeModal/PrimeModal";
import { SideBarContextLink } from "./SideBarContextLink";
import type { NavigationItem, SideBarProps } from "./types";

import styles from "../Menu.module.css";

interface ModalState {
  [id: string]: boolean;
}

/** SideBar - main sidebar with predefined links */
export const SideBar = ({
  minimized = false,
  closeNavigation,
  expandText = "Expand sidebar",
  minimizeText = "Minimize sidebar",
  mainNavigation = [],
  footerNavigation = [],
}: SideBarProps) => {
  const [isMinimized, setIsMinimized] = useState(minimized);
  const [modalsState, setModalsState] = useState<ModalState>({});

  useEffect(() => {
    setIsMinimized(minimized);
  }, [minimized]);

  const renderMenu = (items: Array<NavigationItem>, insideMenu = false) =>
    items.map((navigationItem) => {
      switch (navigationItem.type) {
        case "action":
          return (
            <SideBarLinkButton
              dataCy={navigationItem.dataCy}
              key={`nav_item_${navigationItem.id}`}
              text={
                isMinimized && navigationItem.minimizedText
                  ? decode(navigationItem.minimizedText || "")
                  : decode(navigationItem.text ?? "")
              }
              icon={
                isMinimized && navigationItem.minimizedIcon
                  ? navigationItem.minimizedIcon
                  : (navigationItem.icon ?? "")
              }
              onClick={() => {
                if (closeNavigation !== undefined) {
                  closeNavigation();
                }

                if (navigationItem.action) {
                  navigationItem.action();
                }
              }}
              hideText={isMinimized}
              showTooltip={navigationItem.showTooltip && isMinimized}
            />
          );
        case "context":
          return (
            <Fragment key={`nav_item_${navigationItem.id}`}>
              <SideBarContextLink
                navigationItem={navigationItem}
                renderMenu={renderMenu}
                isMinimized={isMinimized}
              />
            </Fragment>
          );
        case "context_link":
          return (
            <PopupContextMenuLink
              key={`nav_item_${navigationItem.id}`}
              text={decode(navigationItem.text ?? "")}
              to={navigationItem.to ?? ""}
              icon={navigationItem.icon}
              dataCy={navigationItem.dataCy}
              {...(insideMenu ? { role: "menuitem", tabIndex: -1 } : {})}
            />
          );
        case "context_action":
          return (
            <PopupContextMenuLinkButton
              dataCy={navigationItem.dataCy}
              key={`nav_item_${navigationItem.id}`}
              text={decode(navigationItem.text ?? "")}
              icon={navigationItem.icon}
              flag={navigationItem.flag}
              onClick={() => {
                if (closeNavigation !== undefined) {
                  closeNavigation();
                }

                if (navigationItem.action) {
                  navigationItem.action();
                }
              }}
              {...(insideMenu ? { role: "menuitem", tabIndex: -1 } : {})}
            />
          );
        case "context_popup_action": {
          const modalClose = () => {
            setModalsState((prev) => {
              const newState = { ...prev };
              delete newState[navigationItem.id];
              return newState;
            });
          };
          return (
            <Fragment key={`fragment_${navigationItem.id}`}>
              {navigationItem.modal ? (
                <SideBarLinkButton
                  dataCy={navigationItem.dataCy}
                  key={`nav_button_item_${navigationItem.id}`}
                  text={decode(navigationItem.text ?? "")}
                  icon={navigationItem.icon ?? ""}
                  hideText={isMinimized}
                  showTooltip={navigationItem.showTooltip && isMinimized}
                  onClick={() => {
                    closeNavigation?.();
                    navigationItem.action?.();
                    setModalsState((prev) => ({
                      ...prev,
                      [navigationItem.id]: true,
                    }));
                  }}
                />
              ) : (
                <PopupContextMenuLinkButton
                  dataCy={navigationItem.dataCy}
                  key={`nav_link_item_${navigationItem.id}`}
                  text={decode(navigationItem.text ?? "")}
                  icon={navigationItem.icon}
                  onClick={() => {
                    if (closeNavigation !== undefined) {
                      closeNavigation();
                    }
                    if (navigationItem.action) {
                      navigationItem.action();
                    }
                  }}
                />
              )}
              <PrimeModal
                key={`nav_modal_${navigationItem.id}`}
                fixedWidth="576px"
                isOpen={modalsState[navigationItem.id]}
                onClose={modalClose}
                withHeader={!!navigationItem.title}
                header={navigationItem.title}
                withFooter={!!navigationItem.cancelButtonText}
                cancelBtn={{
                  text: navigationItem.cancelButtonText!,
                  variant: "secondary",
                  onClick: modalClose,
                }}
              >
                {navigationItem.content ? <navigationItem.content /> : null}
              </PrimeModal>
            </Fragment>
          );
        }
        case "divider":
          return (
            <SideBarHorizontalDivider
              key={`nav_divider_${navigationItem.id}`}
            />
          );
        default:
          return (
            <SideBarLink
              dataCy="sidebar_link"
              key={`nav_item_${navigationItem.id}`}
              text={decode(navigationItem.text ?? "")}
              to={navigationItem.to ?? ""}
              matchProps={navigationItem.matchProps}
              icon={
                isMinimized && navigationItem.minimizedIcon
                  ? navigationItem.minimizedIcon
                  : navigationItem.icon
              }
              hideText={isMinimized}
              showTooltip={navigationItem.showTooltip && isMinimized}
              onClick={closeNavigation}
            />
          );
      }
    });

  const brandingStore = useBranding();

  return (
    <nav
      className={classnames({
        [styles.sideBar]: true,
        [styles.sideBarMinimized]: isMinimized,
      })}
    >
      {/* Profile top */}
      <div className={styles.sideBarProfileContainerTop}>
        <img
          src={brandingStore.currentFavicon}
          alt="Icon"
          className={styles.sideBarProfileImageSecondary}
        />

        {!isMinimized ? (
          <div className={styles.sideBarProfileName}>
            {brandingStore.currentTitle}
          </div>
        ) : null}
      </div>

      <div key="header_menu" className={styles.sideBarMainContainer}>
        {renderMenu(mainNavigation)}
      </div>

      <div className={styles.sideBarFooterContainer}>
        <ResponsiveLayout
          breakPoint={breakpoints.desktop}
          renderMobile={() => null}
          renderDesktop={() => (
            <SideBarLinkButton
              key="nav_item_toggle"
              text={decode(isMinimized ? expandText : minimizeText)}
              icon={isMinimized ? "expand_menu" : "collapse_menu"}
              onClick={() => {
                setIsMinimized(!isMinimized);
              }}
              hideText={isMinimized}
              showTooltip={isMinimized}
            />
          )}
        />
        {renderMenu(footerNavigation)}
      </div>
    </nav>
  );
};
export default SideBar;
